import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { orcamentos as api, type Cotacao as Resultado, type PedidoDeCotacao } from '../api/orcamentos'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import { marcaDaRota } from './marcaDaRota'
import { mensagemDeErro } from './mensagemDeErro'
import './usuarios.css'
import './simulacao.css'

const PADRAO: PedidoDeCotacao = {
  descricao: '',
  tiragem: 1000,
  largura: null,
  altura: null,
  papel: null,
  gramatura: null,
  paginas: 0,
  formato: null,
  colorido: false,
  capaDura: false,
  ehCaixa: false,
}

/**
 * Cotação por qualquer um dos três motores.
 *
 * A simulação que já existia é de **livro em offset** — era o único motor que
 * o portal tinha. Esta tela chega aos três: a grade das 9 famílias, a fórmula
 * do livro digital e a calculadora de offset, com a árvore escolhendo.
 *
 * 🔴 **A resposta diz qual motor foi usado e por quê.** Sem isso ninguém
 * explica por que dois pedidos parecidos deram preços diferentes — e é a
 * primeira pergunta de quem confere um orçamento.
 */
export function Cotacao() {
  const { t } = useTranslation()
  const marca = marcaDaRota() ?? ''

  const [pedido, setPedido] = useState<PedidoDeCotacao>(PADRAO)
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [calculando, setCalculando] = useState(false)

  const alterar = <C extends keyof PedidoDeCotacao>(campo: C, valor: PedidoDeCotacao[C]) =>
    setPedido({ ...pedido, [campo]: valor })

  const numero = (texto: string) => (texto.trim() === '' ? null : Number(texto.replace(',', '.')))

  async function cotar(evento: FormEvent) {
    evento.preventDefault()

    setErro(null)
    setCalculando(true)

    try {
      setResultado(await api.cotar(marca, pedido))
    } catch (e) {
      setErro(mensagemDeErro(e, t))
      setResultado(null)
    } finally {
      setCalculando(false)
    }
  }

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('cotacao.titulo')}</h1>
          <p className="usuarios__descricao">{t('cotacao.descricao')}</p>
        </div>
      </header>

      <div className="simulacao__colunas">
        <Cartao titulo={t('cotacao.pedido')}>
          <form onSubmit={cotar} noValidate>
            <Campo
              rotulo={t('cotacao.oQueOClienteDisse')}
              value={pedido.descricao}
              onChange={(e) => alterar('descricao', e.target.value)}
              ajuda={t('cotacao.oQueOClienteDisseAjuda')}
              obrigatorio
            />

            <Campo
              rotulo={t('cotacao.tiragem')}
              type="number"
              min="1"
              value={pedido.tiragem}
              onChange={(e) => alterar('tiragem', Number(e.target.value))}
              obrigatorio
            />

            <div className="simulacao__par">
              <Campo
                rotulo={t('cotacao.largura')}
                inputMode="decimal"
                value={pedido.largura ?? ''}
                onChange={(e) => alterar('largura', numero(e.target.value))}
                ajuda={t('cotacao.medidaAjuda')}
              />

              <Campo
                rotulo={t('cotacao.altura')}
                inputMode="decimal"
                value={pedido.altura ?? ''}
                onChange={(e) => alterar('altura', numero(e.target.value))}
              />
            </div>

            <Campo
              rotulo={t('cotacao.papel')}
              value={pedido.papel ?? ''}
              onChange={(e) => alterar('papel', e.target.value || null)}
              ajuda={t('cotacao.papelAjuda')}
            />

            <Campo
              rotulo={t('cotacao.gramatura')}
              type="number"
              value={pedido.gramatura ?? ''}
              onChange={(e) => alterar('gramatura', numero(e.target.value))}
            />

            <fieldset className="usuarios__grupo">
              <legend>{t('cotacao.livro')}</legend>
              <p className="usuarios__descricao">{t('cotacao.livroAjuda')}</p>

              <Campo
                rotulo={t('cotacao.paginas')}
                type="number"
                min="0"
                value={pedido.paginas ?? 0}
                onChange={(e) => alterar('paginas', Number(e.target.value))}
              />

              <Campo
                rotulo={t('cotacao.formato')}
                value={pedido.formato ?? ''}
                onChange={(e) => alterar('formato', e.target.value || null)}
                ajuda={t('cotacao.formatoAjuda')}
              />
            </fieldset>

            <label className="usuarios__opcao">
              <input
                type="checkbox"
                checked={pedido.colorido ?? false}
                onChange={(e) => alterar('colorido', e.target.checked)}
              />
              <span>
                <strong>{t('cotacao.colorido')}</strong>
                <small>{t('cotacao.coloridoAjuda')}</small>
              </span>
            </label>

            <label className="usuarios__opcao">
              <input
                type="checkbox"
                checked={pedido.capaDura ?? false}
                onChange={(e) => alterar('capaDura', e.target.checked)}
              />
              <span>
                <strong>{t('cotacao.capaDura')}</strong>
                <small>{t('cotacao.capaDuraAjuda')}</small>
              </span>
            </label>

            <div className="usuarios__acoes-formulario">
              <Botao type="submit" variante="primario" carregando={calculando}>
                {t('cotacao.cotar')}
              </Botao>
            </div>
          </form>
        </Cartao>

        <Cartao titulo={t('cotacao.resultado')}>
          {erro && <Aviso tom="perigo">{erro}</Aviso>}

          {!resultado && !erro && <Estado tipo="vazio" descricao={t('cotacao.aindaNao')} />}

          {resultado && <Resposta resultado={resultado} />}
        </Cartao>
      </div>
    </main>
  )
}

function Resposta({ resultado }: { resultado: Resultado }) {
  const { t } = useTranslation()

  if (!resultado.cotou)
    return (
      <>
        {/* 🔴 Transbordo não é erro: é uma regra do cliente funcionando. A tela
            diz o motivo com as palavras que vão ao cliente — nunca "não
            fazemos", que é a armadilha 9 do motor v0. */}
        <Aviso tom="alerta" titulo={t('cotacao.vaiParaHumano')}>
          {resultado.explicacao ?? resultado.porque}
        </Aviso>

        <p className="usuarios__descricao">{t('cotacao.porque', { porque: resultado.porque })}</p>
      </>
    )

  return (
    <>
      <Aviso tom="sucesso" titulo={t(`cotacao.motor.${resultado.motor}`)}>
        {t('cotacao.porque', { porque: resultado.porque })}
      </Aviso>

      {resultado.explicacao && <Aviso tom="info">{resultado.explicacao}</Aviso>}

      <p className="simulacao__total">
        {formatar.moeda(resultado.total)}
        <small>{t('cotacao.unitario', { valor: formatar.moeda(resultado.unitario) })}</small>
      </p>

      {resultado.codigo !== null && (
        <p className="usuarios__descricao">
          {t('cotacao.codigo', { codigo: resultado.codigo })}
        </p>
      )}

      {/* A composição item a item é exigência do cliente (ata de 30/06, item 9)
          e guard-rail nosso: sem ela, ninguém sustenta o preço diante de uma
          reclamação. */}
      <div
        className="usuarios__tabela-rolagem"
        tabIndex={0}
        role="group"
        aria-label={t('cotacao.memoriaLegenda')}
      >
        <table className="usuarios__tabela">
          <caption className="usuarios__legenda">{t('cotacao.memoriaLegenda')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('cotacao.etapa')}</th>
              <th scope="col">{t('cotacao.detalhe')}</th>
              <th scope="col">{t('cotacao.valor')}</th>
            </tr>
          </thead>
          <tbody>
            {resultado.memoria.map((linha, i) => (
              <tr key={`${linha.etapa}-${i}`}>
                <th scope="row">{linha.etapa}</th>
                <td>
                  {linha.descricao}
                  {linha.origem && <small> · {linha.origem}</small>}
                </td>
                <td>{linha.valor === 0 ? '—' : formatar.numero(linha.valor, { maximumFractionDigits: 4 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="usuarios__descricao">
        {t('cotacao.versao', { versao: resultado.versaoDaTabela })}
      </p>
    </>
  )
}
